import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

/**
 * Contextual header controls owned by the active page (currently Results):
 * the search-edit trigger and the Compare trigger. The global Header renders
 * them, but the handlers live on the page that has the search state.
 */
export interface HeaderActions {
  onEditSearch?: () => void;
  onOpenCompare?: () => void;
  canCompare?: boolean;
}

interface HeaderActionsValue {
  hasEdit: boolean;
  hasCompare: boolean;
  canCompare: boolean;
  editSearch: () => void;
  openCompare: () => void;
  register: (actions: HeaderActions) => void;
}

const HeaderActionsContext = createContext<HeaderActionsValue | null>(null);

export const HeaderActionsProvider = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HeaderActions>({});
  const [flags, setFlags] = useState({ hasEdit: false, hasCompare: false, canCompare: false });

  // Stable registrar. Updates the ref every call so dispatchers always invoke
  // the latest handler, but only bumps state when the rendered flags change —
  // this keeps unstable handler identities from causing a render loop.
  const register = useCallback((actions: HeaderActions) => {
    ref.current = actions;
    const next = {
      hasEdit: Boolean(actions.onEditSearch),
      hasCompare: Boolean(actions.onOpenCompare),
      canCompare: Boolean(actions.canCompare),
    };
    setFlags((prev) => (
      prev.hasEdit === next.hasEdit
        && prev.hasCompare === next.hasCompare
        && prev.canCompare === next.canCompare
        ? prev
        : next
    ));
  }, []);

  const editSearch = useCallback(() => ref.current.onEditSearch?.(), []);
  const openCompare = useCallback(() => ref.current.onOpenCompare?.(), []);

  const value = useMemo<HeaderActionsValue>(() => ({
    ...flags, editSearch, openCompare, register,
  }), [flags, editSearch, openCompare, register]);

  return (
    <HeaderActionsContext.Provider value={value}>
      {children}
    </HeaderActionsContext.Provider>
  );
};

const useHeaderActionsContext = (): HeaderActionsValue => {
  const ctx = useContext(HeaderActionsContext);
  if (!ctx) throw new Error('useHeaderActions must be used within a HeaderActionsProvider');
  return ctx;
};

/** Header consumer: flags + stable dispatchers for the contextual controls. */
export const useHeaderActions = useHeaderActionsContext;

/** Page registrar: publish edit/compare handlers to the header while mounted. */
export const useRegisterHeaderActions = (actions: HeaderActions): void => {
  const { register } = useHeaderActionsContext();
  const { onEditSearch, onOpenCompare, canCompare } = actions;

  // Hold the latest handlers so the stable wrappers below always dispatch to the
  // current closures without forcing a re-register.
  const latest = useRef<HeaderActions>(actions);
  latest.current = actions;

  // Identity-stable dispatch wrappers; only the *presence* of a handler (and
  // canCompare) drives the rendered flags, so the effect keys on those booleans
  // rather than the ever-changing handler identities. Passing the raw closures
  // here would re-run the effect every render, and its cleanup/re-register
  // toggle would bump the provider's flags object on each pass → render loop.
  const editWrapper = useRef(() => latest.current.onEditSearch?.());
  const compareWrapper = useRef(() => latest.current.onOpenCompare?.());

  const hasEdit = Boolean(onEditSearch);
  const hasCompare = Boolean(onOpenCompare);

  useEffect(() => {
    register({
      onEditSearch: hasEdit ? editWrapper.current : undefined,
      onOpenCompare: hasCompare ? compareWrapper.current : undefined,
      canCompare,
    });
    return () => register({});
  }, [register, hasEdit, hasCompare, canCompare]);
};
